import dayjs from "dayjs";
import type { PassageApiItem, PassageKindUi } from "./planningTypes";

export const STATUTS_PASSAGE_FILTRE = [
  "",
  "PLANIFIE",
  "CONFIRME",
  "EN_ROUTE",
  "EN_COURS",
  "EFFECTUE",
  "PARTIEL",
  "ANNULE",
  "REPORTE",
  "NON_EFFECTUE",
  "EN_ATTENTE_VALIDATION",
  "CLOSED_WITH_ANOMALIES",
] as const;

export const STATUTS_TOURNEE_FILTRE = [
  "",
  "BROUILLON",
  "PLANIFIEE",
  "EN_COURS",
  "TERMINEE",
  "ANNULEE",
] as const;

export function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Planning statut aligné sur l’exécution terrain si `statut` était resté en retard (ex. REALISE + PLANIFIE). */
export function effectiveStatutUpper(p: {
  statut: string;
  executionStatus?: string | null;
  dateRealisee?: string | null;
}): string {
  const st = p.statut.toUpperCase();
  if (st === "CLOSED_WITH_ANOMALIES") return "CLOSED_WITH_ANOMALIES";
  if (
    st === "EFFECTUE" ||
    st === "PARTIEL" ||
    st === "EN_ATTENTE_VALIDATION" ||
    st === "NON_EFFECTUE" ||
    st === "ANNULE"
  ) {
    return st;
  }
  const ex = p.executionStatus?.toUpperCase();
  if (ex === "REALISE") return "EN_ATTENTE_VALIDATION";
  if (ex === "ECHEC") return "NON_EFFECTUE";
  if (ex === "EN_COURS") return "EN_COURS";
  if (st === "PLANIFIE" && p.dateRealisee) return "EN_ATTENTE_VALIDATION";
  return st;
}

export function clientLabel(p: PassageApiItem): string {
  const c = p.client;
  if (!c) return p.clientId != null ? `Client #${p.clientId}` : "—";
  const nom =
    (c.nomEntreprise?.trim() ||
      [c.nom, c.prenom].filter(Boolean).join(" ").trim()) ||
    "";
  return nom || `Client #${c.id}`;
}

export function passageKind(p: PassageApiItem): PassageKindUi {
  if (p.isSuspect) return "urgence";
  return "normal";
}

export function passageKindLabel(k: PassageKindUi): string {
  if (k === "urgence") return "Urgence";
  if (k === "portail_inconnu") return "Portail (non exposé)";
  return "Normal";
}

export function statutBadgeClass(statut: string): string {
  const s = statut.toUpperCase();
  if (s === "EFFECTUE" || s === "TERMINEE") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (s === "EN_COURS" || s === "EN_ROUTE" || s === "PLANIFIEE" || s === "CONFIRME")
    return "bg-sky-500/15 text-sky-200 ring-sky-500/25";
  if (s === "CLOSED_WITH_ANOMALIES") return "bg-amber-500/15 text-amber-200 ring-amber-500/30";
  if (s === "EN_ATTENTE_VALIDATION") return "bg-amber-500/15 text-amber-200 ring-amber-500/25";
  if (s === "ANNULE" || s === "ANNULEE" || s === "NON_EFFECTUE")
    return "bg-slate-600/40 text-slate-300 ring-slate-500/30";
  if (s === "REPORTE") return "bg-violet-500/15 text-violet-200 ring-violet-400/25";
  return "bg-slate-700/60 text-slate-200 ring-slate-600/40";
}

export function isLatePassage(p: PassageApiItem): boolean {
  const endOfToday = dayjs().endOf("day");
  const prevue = dayjs(p.datePrevue);
  if (!prevue.isValid()) return false;
  const terminal = new Set([
    "EFFECTUE",
    "PARTIEL",
    "NON_EFFECTUE",
    "ANNULE",
    "ANNULEE",
    "TERMINEE",
    "EN_ATTENTE_VALIDATION",
    "CLOSED_WITH_ANOMALIES",
  ]);
  if (terminal.has(effectiveStatutUpper(p))) return false;
  return prevue.isBefore(endOfToday.subtract(1, "day")); // strict before calendar yesterday boundary — simplified: overdue if date < today start
}

export function computeKpis(rowsToday: PassageApiItem[], rowsPool: PassageApiItem[]): {
  passagesAujourdhui: number;
  nonAffectes: number;
  enRetard: number;
  urgencesClient: number;
  enCours: number;
  validesFacturables: number;
} {
  const passagesAujourdhui = rowsToday.length;

  const nonAffectes = rowsPool.filter((p) => !p.tourneeId).length;

  const enRetard = rowsPool.filter((p) => isLatePassage(p)).length;

  const urgencesClient = rowsPool.filter((p) => Boolean(p.isSuspect)).length;

  const enCours = rowsPool.filter((p) => {
    const s = effectiveStatutUpper(p);
    return s === "EN_COURS" || s === "EN_ROUTE";
  }).length;

  const validesFacturables = rowsPool.filter((p) => {
    const s = effectiveStatutUpper(p);
    return (s === "EFFECTUE" || s === "EN_ATTENTE_VALIDATION") && p.isFacturable;
  }).length;

  return {
    passagesAujourdhui,
    nonAffectes,
    enRetard,
    urgencesClient,
    enCours,
    validesFacturables,
  };
}

/** Libellés lecture opérationnelle — priorité au statut opérationnel serveur puis admin / display. */
export function passageStatutLecture(p: PassageApiItem): { title: string; subtitle?: string } {
  const opLabel = (p as PassageApiItem & { operationalStatusLabel?: string | null }).operationalStatusLabel?.trim();
  const adminLabel = (p as PassageApiItem & { adminStatusLabel?: string | null }).adminStatusLabel?.trim();
  const label = opLabel || adminLabel || p.displayLabel?.trim();
  const ds = (p.displayStatus ?? "").toUpperCase();

  if (label) {
    if (ds === "CLOSED_WITH_ANOMALIES") {
      const qt = (p.qualityTier ?? p.qualityStatus ?? "").toUpperCase();
      const qualHint =
        qt === "OK"
          ? undefined
          : qt === "CRITICAL"
            ? "Qualité critique"
            : "Clôturé avec anomalies qualité";
      const billParts = [p.billingLabel && `Facturation : ${p.billingLabel}`, p.billingReason && `Raison : ${p.billingReason}`].filter(
        Boolean
      ) as string[];
      const subtitle = [qualHint, ...billParts].filter(Boolean).join(" · ");
      return subtitle ? { title: label, subtitle } : { title: label };
    }
    return { title: label };
  }

  const raw = (p.statut ?? "").toUpperCase();
  const tourneeExec = (p.tournee?.executionStatus ?? "").toUpperCase();
  const tourneeSt = (p.tournee?.statut ?? "").toUpperCase();

  if (raw === "CLOSED_WITH_ANOMALIES") {
    const billParts = [p.billingLabel && `Facturation : ${p.billingLabel}`, p.billingReason && `Raison : ${p.billingReason}`].filter(
      Boolean
    ) as string[];
    return {
      title: "Clôturé avec anomalies",
      subtitle: billParts.length ? billParts.join(" · ") : undefined,
    };
  }
  if (raw === "EFFECTUE" || raw === "PARTIEL") {
    return { title: "Validé" };
  }
  if (raw === "NON_EFFECTUE" || raw === "ANNULE") {
    return { title: "Rejeté" };
  }
  if (raw === "EN_ATTENTE_VALIDATION") {
    if (tourneeExec === "TERMINEE" || tourneeSt === "TERMINEE") {
      return {
        title: "Clôturé avec anomalies",
        subtitle: p.isFacturable ? undefined : "Non facturable — preuves/GPS incomplets",
      };
    }
    return { title: "En attente validation" };
  }

  const eff = effectiveStatutUpper(p);
  return { title: eff.replace(/_/g, " ") };
}

/** Classes ring/badge pour la colonne Statut (planning / passages). */
export function passageStatutLectureBadgeClass(p: PassageApiItem): string {
  const opTone = (p as PassageApiItem & { operationalStatusTone?: string | null }).operationalStatusTone?.toLowerCase();
  if (opTone === "danger") {
    return "bg-rose-500/15 text-rose-100 ring-rose-500/35";
  }
  if (opTone === "warning") {
    return "bg-amber-500/15 text-amber-100 ring-amber-500/30";
  }
  if (opTone === "success") {
    return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  }
  if (opTone === "neutral") {
    return "bg-slate-700/60 text-slate-200 ring-slate-600/40";
  }

  const tone = (p as PassageApiItem & { adminStatusTone?: string | null }).adminStatusTone?.toLowerCase();
  if (tone === "danger") {
    return "bg-rose-500/15 text-rose-100 ring-rose-500/35";
  }
  if (tone === "warning") {
    return "bg-amber-500/15 text-amber-100 ring-amber-500/30";
  }

  const ds = (p.displayStatus ?? "").toUpperCase();

  if (ds === "OVERDUE_ACTION") {
    return "bg-orange-600/20 text-orange-100 ring-orange-500/40";
  }

  if (ds === "VALIDATED_CLOSED" || ds === "VALIDATED") {
    return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  }
  if (ds === "REJECTED_CLOSED" || ds === "REJECTED") {
    return "bg-rose-500/15 text-rose-200 ring-rose-500/25";
  }
  if (ds === "CLOSED_WITH_ANOMALIES") {
    const qt = (p.qualityTier ?? p.qualityStatus ?? "").toUpperCase();
    return qt === "CRITICAL"
      ? "bg-rose-500/15 text-rose-100 ring-rose-500/30"
      : "bg-amber-500/15 text-amber-200 ring-amber-500/30";
  }
  if (ds === "PENDING_VALIDATION") {
    return "bg-amber-500/15 text-amber-200 ring-amber-500/25";
  }
  if (ds === "PLANNED") {
    return "bg-sky-500/15 text-sky-200 ring-sky-500/25";
  }
  if (ds === "IN_PROGRESS") {
    return "bg-sky-500/15 text-sky-200 ring-sky-500/25";
  }
  if (ds === "REALISED_FIELD") {
    return "bg-teal-500/15 text-teal-200 ring-teal-500/25";
  }

  const raw = (p.statut ?? "").toUpperCase();
  const tourneeExec = (p.tournee?.executionStatus ?? "").toUpperCase();
  const tourneeSt = (p.tournee?.statut ?? "").toUpperCase();

  if (raw === "CLOSED_WITH_ANOMALIES") {
    const qt = (p.qualityTier ?? p.qualityStatus ?? "").toUpperCase();
    return qt === "CRITICAL"
      ? "bg-rose-500/15 text-rose-100 ring-rose-500/30"
      : "bg-amber-500/15 text-amber-200 ring-amber-500/30";
  }
  if (raw === "EFFECTUE" || raw === "PARTIEL") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (raw === "NON_EFFECTUE" || raw === "ANNULE") return "bg-rose-500/15 text-rose-200 ring-rose-500/25";
  if (raw === "EN_ATTENTE_VALIDATION" && (tourneeExec === "TERMINEE" || tourneeSt === "TERMINEE")) {
    const qt = (p.qualityTier ?? p.qualityStatus ?? "").toUpperCase();
    return qt === "CRITICAL"
      ? "bg-rose-500/15 text-rose-100 ring-rose-500/30"
      : "bg-amber-500/15 text-amber-200 ring-amber-500/30";
  }
  if (raw === "EN_ATTENTE_VALIDATION") return "bg-amber-500/15 text-amber-200 ring-amber-500/25";

  return statutBadgeClass(effectiveStatutUpper(p));
}
